import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    heading = "heading",
    content = "content",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    checkboxFieldValue = "checkboxFieldValue",
    buttonLabelColor = "buttonLabelColor",
    buttonBackgroundColor = "buttonBackgroundColor",
    Color = "Color",
    margin = "margin",
}

interface OwnProps extends WidgetProps {
    fields: {
        background: any;
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.content]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.checkboxFieldValue]: boolean;
        [fields.buttonLabel]: string;
        [fields.Color]: string;
        [fields.margin]: string;
        [fields.buttonLabelColor]: string;
        [fields.buttonBackgroundColor]: string;
    };
}

export interface CENBannerwithdownloadbutton {
    downloadLineCardSection?: InjectableCss;
    btnDownload?: LinkPresentationProps;
    downloadleftwrapper?: InjectableCss;
    downloadBtnWrapper?: InjectableCss;
    colorPickerStyles?: InjectableCss;
    titleText?: TypographyProps;
}

export const cENBannerwithdownloadbutton: CENBannerwithdownloadbutton = {
    downloadleftwrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 67%;
                    `,
                    css`
                        flex-basis: 67%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    downloadBtnWrapper: {
        css: css`
            flex: 1;
            justify-content: flex-end;
            display: inline-flex;
            margin-top: 20px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 1;
                        justify-content: flex-end;
                        display: inline-flex;
                    `,
                    css`
                        flex: 1;
                        justify-content: flex-end;
                        display: inline-flex;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            color: #ffffff;
        `,
    },
};

const styles = cENBannerwithdownloadbutton;
export const CENBannerwithdownloadbutton: FC<OwnProps> = ({ id, fields }) => {
    const { url } = useGetLink(fields.buttonLink);

    const btnDownload = {
        css: css`
            padding: 8px 16px;
            width: 139px;
            height: 32px;
            background: ${fields.buttonBackgroundColor};
            border-radius: 16px;
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            color: ${fields.buttonLabelColor};
            span {
                color: ${fields.buttonLabelColor} !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 15px;
                    `,
                    css`
                        margin-top: 15px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    };
    const backgroundColor = {
        css: css`
            background: ${fields.backgroundColor};
        `,
    };

    const colorPickerStyles = {
        css: css`
            color: ${fields.Color};
        `,
    };
    const downloadLineCardSection = {
        css: css`
            display: flex;
            line-height: 28px;
            padding: 50px 100px;
            box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
            color: #fff;
            margin: ${fields.margin} !important;

            h3 {
                color: #fff;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: center;
                        padding: 20px;
                        margin-bottom: 40px !important;
                        flex-direction: column;

                        p {
                            line-height: 20px;
                        }
                        ,
                        h3 {
                            font-size: 18px;
                            line-height: 24px;
                        }
                    `,
                    css`
                        align-items: center;
                        padding: 20px;
                        margin-bottom: 40px !important;
                        flex-direction: column;
                        p {
                            line-height: 20px;
                        }
                        ,
                        h3 {
                            font-size: 18px;
                            line-height: 24px;
                        }
                    `,
                    css`
                        padding: 40px 63px;
                    `,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <>
            <StyledWrapper {...backgroundColor}>
                <StyledWrapper {...downloadLineCardSection}>
                    <StyledWrapper {...styles.downloadleftwrapper}>
                        <Typography {...styles.titleText}> {fields.heading}</Typography>
                        <Typography as="p" {...colorPickerStyles}>
                            {fields.content}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.downloadBtnWrapper}>
                        <Link href={url} {...btnDownload} target={fields.checkboxFieldValue ? "_blank" : "_self"}>
                            {fields.buttonLabel}
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: CENBannerwithdownloadbutton,
    definition: {
        group: "Basic",
        displayName: "CENBannerwithdownloadbutton",
        fieldDefinitions: [
            {
                name: fields.heading,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.content,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                editorTemplate: "LinkField",
                displayName: "Button Link",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.checkboxFieldValue,
                displayName: "Open in a new tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.buttonLabel,
                displayName: "Button Label",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.buttonLabelColor,
                editorTemplate: "ColorPickerField",
                displayName: "Button Label color",
                defaultValue: "#8f0446",
                fieldType: "General",
            },
            {
                name: fields.buttonBackgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Button Background color",
                defaultValue: "white",
                fieldType: "General",
            },
            {
                name: fields.backgroundColor,
                editorTemplate: "TextField",
                displayName: "Background-color",
                defaultValue: "maroon",
                fieldType: "General",
            },
            {
                name: fields.Color,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "white",
                fieldType: "General",
            },
            {
                name: fields.margin,
                editorTemplate: "TextField",
                displayName: "Margin",
                defaultValue: "30px 0px 100px",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
